import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CustomTableSortOrder,
  CustomTableSortState
} from '@components/custom-table/types';
import { LEAD_MANAGEMENT } from '@constants/routes';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import DashboardDateRangePicker from '@features/dashboard/components/dashboard-date-range-picker/DashboardDateRangePicker';
import {
  DashboardDateRange,
  DashboardDateRangePickerTypesEnum
} from '@features/dashboard/constants';
import { DateRangeType } from '@features/dashboard/types';
import Head from '@layouts/head/Head';
import { getLocalStorageItem } from '@utils/storage';

import SourceDistributionTable from '../components/sources/SourceDistributionTable';
import SourceInsightCardsRow from '../components/sources/SourceInsightCardsRow';
import { dateRangeMap } from '../constants';
import { SOURCES_PAGE_LIMIT } from '../constants/leadSources';
import {
  useGetAdSourceDistributionsQuery,
  useGetAdSourceInsightsQuery
} from '../queries/LeadManagementQuery';
import convertDashboardDateRangeToIso from '../utils/leadDateRange';

const EMPTY_RANGE: DateRangeType = { startDate: '', endDate: '' };

const LeadAdSources = () => {
  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );
  const navigate = useNavigate();
  const [pickerType, setPickerType] = useState<string>(
    DashboardDateRangePickerTypesEnum.thisMonth
  );
  const [pickerRange, setPickerRange] = useState<DateRangeType>(EMPTY_RANGE);
  const [page, setPage] = useState(0);
  const [sortState, setSortState] = useState<CustomTableSortState | null>(null);

  const rangeParams = useMemo(() => {
    const range = dateRangeMap[pickerType as DashboardDateRangePickerTypesEnum];
    if (pickerType === DashboardDateRangePickerTypesEnum.custom) {
      const { from, to } = convertDashboardDateRangeToIso(pickerRange);
      return { range, from, to };
    }
    return { range };
  }, [pickerType, pickerRange]);

  const skipQueries =
    !organizationId ||
    (rangeParams.range === 'custom' && (!rangeParams.from || !rangeParams.to));

  const sharedArgs = {
    organizationId: organizationId || '',
    range: rangeParams.range,
    from: rangeParams.from,
    to: rangeParams.to
  };

  const {
    data: summary,
    isLoading: isInsightsLoading,
    isError: isInsightsError
  } = useGetAdSourceInsightsQuery(sharedArgs, { skip: skipQueries });

  const {
    data: distribution,
    isLoading: isDistributionLoading,
    isError: isDistributionError
  } = useGetAdSourceDistributionsQuery(
    {
      ...sharedArgs,
      page,
      limit: SOURCES_PAGE_LIMIT,
      ...(sortState
        ? { sort_by: sortState.sortBy, sort_order: sortState.sortOrder }
        : {})
    },
    { skip: skipQueries }
  );

  const handleDateRangeSelect = (
    dateRange: DashboardDateRange,
    dateRangeType: DashboardDateRangePickerTypesEnum
  ) => {
    setPickerType(dateRangeType);
    setPickerRange(dateRange);
    setPage(0);
  };

  const handleSortChange = (
    sortKey: string,
    sortOrder: CustomTableSortOrder
  ) => {
    setSortState({ sortBy: sortKey, sortOrder });
    setPage(0);
  };

  return (
    <div className="-mb-15 flex w-full flex-col gap-4 pt-6">
      <Head title="Ad Sources" />
      <div className="flex w-full flex-row flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            type="button"
            aria-label="Back to lead sources"
            onClick={() => navigate(LEAD_MANAGEMENT.LEAD_SOURCES)}
            className="flex items-center justify-center bg-transparent p-1.5"
          >
            <div className="size-2.5 -rotate-45 border-l border-t border-cofeeGreyBlue700" />
          </button>
          <h1 className="text-2xl font-bold leading-8 text-cofeeNeutral10">
            Ad Sources
          </h1>
        </div>
        <DashboardDateRangePicker
          minimumDateDifference={1}
          handleDateRangeSelect={handleDateRangeSelect}
        />
      </div>
      <SourceInsightCardsRow
        summary={summary}
        isLoading={isInsightsLoading}
        isError={isInsightsError}
        hideSourceLogo
      />
      <SourceDistributionTable
        sources={distribution?.ads ?? []}
        totalCount={distribution?.pagination?.total_rows ?? 0}
        page={page}
        sortState={sortState}
        isLoading={isDistributionLoading}
        isError={isDistributionError}
        isAdSource
        onPageChange={setPage}
        onSortChange={handleSortChange}
      />
    </div>
  );
};

export default LeadAdSources;
